/**
 * OutreachRowsTable — the Figma inbox list: a card with a count/toolbar strip
 * ("N creators", or BulkToolbar while rows are selected) over the table's own
 * column-header row (Creator · Message · Contact · Outreach · Last activity,
 * select-all checkbox included) and its rows of creator · message · contact ·
 * pills · time · menu. Reuses the shared DataTable.
 *
 * Figma's own header component (node 505:49954, "Thread / Table Header")
 * carries only select-all + the row count — no column-name labels. Showing
 * them here is a deliberate deviation from that reference, per explicit user
 * ask ("why don't we have column names at top") — not a Figma-match fix.
 *
 * The selection drives BulkToolbar: "Bulk send (N)" (With brand: "Move to brand dashboard (N)"), "Export CSV" and "Remove".
 * Both the bulk send and the per-row send hand the SAME
 * `onSendFirstTouch(entryIds)` callback up, so the tab opens one dialog
 * either way and the single-vs-batch route is decided by how many ids
 * arrive; bulk Remove works the same way through `onRemove(entryIds)`.
 */
import { useMemo } from 'react';
import { DataTable, type ColumnDef } from '../../../components/common/DataTable';
import { EmptyState } from '../../../components/common/EmptyState';
import { FileText } from '../../../design-system/icons';
import { ContactCell } from './ContactCell';
import { InboxCreatorCell, InboxLastActivityCell, InboxMessageCell, InboxStatusCell } from './InboxRowCells';
import { RowActions, canSendFirstTouch, firstTouchBlocker } from './RowActions';
import { StatusTooltipScope } from './StatusTooltip';
import { isInOutreach } from './handoffStates';
import { BulkToolbar, type BulkMove } from './BulkToolbar';
import type { FindContactPhase } from '@discovr/features';
import type { OutreachInboxTab, OutreachRow } from '@discovr/types';
import gmailLogo from '../../../assets/connectors/gmail.png';

// Figma's inbox row is 68px with its divider inside: the row's border adds 2px, so 10px below instead of 12.
const ROW_Y = 'pt-3 pb-2.5';

export interface OutreachRowsTableProps {
  rows: OutreachRow[];
  loading: boolean;
  selectedIds: string[];
  onToggleSelect: (entryId: string) => void;
  onToggleSelectAll: () => void;
  onOpenRow: (entryId: string) => void;
  onFindContact: (entryId: string) => void;
  /** The row's own Find contact hunt (useFindContactStatus); absent means no phase UI. */
  findContactPhase?: (entryId: string) => FindContactPhase | undefined;
  findContactFailure?: (entryId: string) => string | undefined;
  onEditContact: (entryId: string) => void;
  /** A row whose first-touch draft waits for an email (draftWaitingEmail): its click opens that draft. */
  onOpenWaitingDraft: (entryId: string) => void;
  /** Row action, bulk action, or a contactable row's own Contact-cell click
   *  alike — one id (as a single-element array), or the whole selection. */
  onSendFirstTouch: (entryIds: string[]) => void;
  onViewProfile: (entryId: string) => void;
  onMarkRead: (entryId: string) => void;
  onMarkUnread: (entryId: string) => void;
  onPauseFollowups: (entryId: string) => void;
  onResumeFollowups: (entryId: string) => void;
  onSendToBrand: (entryId: string) => void;
  onSuppress: (entryId: string) => void;
  onUnsuppress: (entryId: string) => void;
  /** Row action and bulk action alike — one id, or the whole selection. */
  onRemove: (entryIds: string[]) => void;
  onClearEscalation?: (entryId: string) => void;
  onReopen?: (entryId: string) => void;
  onNotUnsubscribe?: (entryId: string) => void;
  onClearSelection: () => void;
  /** Bulk "Find contacts" (R-6), passed straight to BulkToolbar. */
  findContactsCount?: number;
  onFindContacts?: () => void;
  findContactsBusy?: boolean;
  /** With brand tab only (D1). */
  moveToBrand?: BulkMove;
  /** U6 Retry: per row (menu) and over the selection (bulk bar). */
  onRetry?: (entryId: string, messageId: number) => void;
  onStop?: (entryId: string, messageId: number) => void;
  onDiscardDraft?: (entryId: string, messageId: number) => void;
  retryCount?: number;
  onRetryMany?: () => void;
  retryBusy?: boolean;
  /** Why fewer of the selection enter the bulk batch than are retry-eligible. */
  retryNote?: string;
  canWrite: boolean;
  /** The campaign's `sendTz` — scheduled times show in it; the creator's own zone is reference only (U2). */
  campaignZone?: string;
  /** The campaign's Automatic follow-ups switch (default ON); OFF hides the automatic-step countdown. */
  autoFollowups?: boolean;
  /** Total creators matching the current filter (across all pages) — shown
   *  in the card header above the rows, e.g. "3 creators". */
  totalCount: number;
  /** Selected creators a first touch can actually go to (A5). */
  sendableCount?: number;
  sendDisabledReason?: string;
  sendNote?: string;
  /** The Inbox tab on screen: the Contacted tab drops the redundant "Contacted" pill (S1-F9). */
  activeTab?: OutreachInboxTab;
  /** Whether a search query or an active FilterPanel filter is narrowing
   *  this tab — tags the header "· filtered" and swaps the zero-row empty
   *  state to Figma's "No match" (icon/title/body/Clear filters), distinct
   *  from a tab that's genuinely empty (handled one level up by
   *  OutreachTab's own rich EmptyState, this component never sees that
   *  case with `filtered` false and zero rows at the same time). */
  filtered: boolean;
  onClearFilters: () => void;
}

/** Ids present in exactly one of the two sets. */
function symmetricDifference(a: ReadonlySet<string>, b: ReadonlySet<string>): string[] {
  const diff: string[] = [];
  a.forEach((id) => {
    if (!b.has(id)) diff.push(id);
  });
  b.forEach((id) => {
    if (!a.has(id)) diff.push(id);
  });
  return diff;
}

/** Both the header select-all checkbox and each row's own checkbox drive the
 *  same DataTable `onSelectionChange(next)` callback — this tells them apart
 *  from the *next* Set alone, mirroring `toggleSelectAll`'s own semantics
 *  (partial/none of this page → all of it; all of it → none) rather than
 *  diffing by size alone, which false-positives as a single-row toggle once
 *  only one page id was left to flip (e.g. 4 of 5 already selected). Guarded
 *  to pages with >1 row: on a one-row page a genuine single-row toggle can't
 *  be told apart from select-all at all (same resulting Set either way), so
 *  that case is left to fall through to the plain per-row path below —
 *  onToggleSelect/onToggleSelectAll are equivalent there regardless. */
function isSelectAllTransition(pageIds: string[], selectedSet: ReadonlySet<string>, next: ReadonlySet<string>): boolean {
  if (pageIds.length <= 1) return false;
  const wasFullySelected = pageIds.every((id) => selectedSet.has(id));
  const isFullySelected = pageIds.every((id) => next.has(id));
  const isFullyCleared = pageIds.every((id) => !next.has(id));
  return (!wasFullySelected && isFullySelected) || (wasFullySelected && isFullyCleared);
}

type RowClickAction = 'wizard' | 'waiting_draft' | 'add_email' | 'profile' | 'thread';

const ROW_CLICK_LABELS: Record<RowClickAction, string> = {
  wizard: 'Open send panel for',
  waiting_draft: 'Open draft for',
  add_email: 'Add an email for',
  profile: 'Open creator details for',
  thread: 'Open thread with',
};

/** Owner rulings 2026-10-09: a not-contacted row opens the wizard, or the add-email dialog when it has no email; read-only
 *  staff get the creator sidebar instead. Do not contact and already-queued rows, and every contacted row, open the thread. */
function rowClickAction(row: OutreachRow, canWrite: boolean): RowClickAction {
  const notContacted = isInOutreach(row) && row.outreachState === 'sourced';
  if (!canWrite) return notContacted ? 'profile' : 'thread';
  if (notContacted && row.draftWaitingEmail) return 'waiting_draft';
  if (canSendFirstTouch(row)) return 'wizard';
  if (notContacted && firstTouchBlocker(row) === 'no_contact') return 'add_email';
  return 'thread';
}

function OutreachRowsTableBody({
  rows,
  loading,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onOpenRow,
  onFindContact,
  findContactPhase,
  findContactFailure,
  onEditContact,
  onOpenWaitingDraft,
  onSendFirstTouch,
  onViewProfile,
  onMarkRead,
  onMarkUnread,
  onPauseFollowups,
  onResumeFollowups,
  onSendToBrand,
  onSuppress,
  onUnsuppress,
  onRemove,
  onClearEscalation,
  onReopen,
  onNotUnsubscribe,
  onClearSelection,
  findContactsCount,
  onFindContacts,
  findContactsBusy,
  moveToBrand,
  onRetry,
  onStop,
  onDiscardDraft,
  retryCount,
  onRetryMany,
  retryBusy,
  retryNote,
  canWrite,
  campaignZone,
  autoFollowups,
  totalCount,
  sendableCount,
  sendDisabledReason,
  sendNote,
  activeTab,
  filtered,
  onClearFilters,
}: OutreachRowsTableProps) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const columns = useMemo<ColumnDef<OutreachRow>[]>(
    () => [
      {
        id: 'creator',
        cellClassName: ROW_Y,
        header: 'Creator',
        minWidth: 200,
        sticky: 'left',
        alwaysVisible: true,
        cell: (row) => <InboxCreatorCell row={row} onViewProfile={onViewProfile} />,
      },
      {
        id: 'message',
        cellClassName: ROW_Y,
        header: 'Message',
        minWidth: 160,
        grow: true,
        cell: (row) => <InboxMessageCell row={row} />,
      },
      {
        id: 'contact',
        cellClassName: ROW_Y,
        header: 'Contact',
        minWidth: 150,
        maxWidth: 260,
        wrap: true,
        cell: (row) => (
          <ContactCell
            row={row}
            canWrite={canWrite}
            onFindContact={onFindContact}
            findContactPhase={findContactPhase?.(row.entryId)}
            findContactFailure={findContactFailure?.(row.entryId)}
            onEditContact={onEditContact}
            onSendFirstTouch={(entryId) => onSendFirstTouch([entryId])}
          />
        ),
      },
      {
        id: 'state',
        cellClassName: ROW_Y,
        header: 'Outreach',
        // Shares spare width with Message so three pills stay on one line, Figma's 68px; 150 keeps narrow screens fitting.
        minWidth: 150,
        grow: true,
        wrap: true,
        align: 'right',
        cell: (row) => <InboxStatusCell row={row} campaignZone={campaignZone} hideContactedPill={activeTab === 'contacted'} autoFollowups={autoFollowups} />,
      },
      {
        id: 'time',
        cellClassName: ROW_Y,
        header: 'Last activity',
        minWidth: 120,
        wrap: true,
        align: 'right',
        cell: (row) => <InboxLastActivityCell row={row} campaignZone={campaignZone} autoFollowups={autoFollowups} />,
      },
      {
        id: 'actions',
        cellClassName: ROW_Y,
        header: '',
        minWidth: 56,
        align: 'right',
        alwaysVisible: true,
        cell: (row) => (
          <RowActions
            row={row}
            canWrite={canWrite}
            onOpenRow={onOpenRow}
            onSendFirstTouch={onSendFirstTouch}
            onViewProfile={onViewProfile}
            onMarkRead={onMarkRead}
            onMarkUnread={onMarkUnread}
            onPauseFollowups={onPauseFollowups}
            onResumeFollowups={onResumeFollowups}
            onSendToBrand={onSendToBrand}
            onSuppress={onSuppress}
            onUnsuppress={onUnsuppress}
            onRemove={onRemove}
            onClearEscalation={onClearEscalation}
            onReopen={onReopen}
            onNotUnsubscribe={onNotUnsubscribe}
            onRetry={onRetry}
            onStop={onStop}
            onDiscardDraft={onDiscardDraft}
            retryBusy={retryBusy}
          />
        ),
      },
    ],
    [
      onFindContact,
      findContactPhase,
      findContactFailure,
      onEditContact,
      onOpenRow,
      onSendFirstTouch,
      onViewProfile,
      onMarkRead,
      onMarkUnread,
      onPauseFollowups,
      onResumeFollowups,
      onSendToBrand,
      onSuppress,
      onUnsuppress,
      onRemove,
      onClearEscalation,
      onReopen,
      onNotUnsubscribe,
      onRetry,
      onStop,
      onDiscardDraft,
      retryBusy,
      canWrite,
      campaignZone,
      autoFollowups,
      activeTab,
    ],
  );

  const pageIds = useMemo(() => rows.map((row) => row.entryId), [rows]);
  const selectingAny = selectedIds.length > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Bordered card (SettingsSection's card tokens). DataTable renders
          border-0/rounded-none, so this card's own border is the only one
          shown — the table's own column-header row (below) supplies the
          Creator/Message/Contact/Outreach/Last activity labels and the
          select-all checkbox; this strip is just the count/BulkToolbar. */}
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex min-h-14 items-center justify-between gap-3 border-b border-border bg-(--stone-50) px-4 py-2">
          {selectingAny ? (
            <BulkToolbar
              selectedCount={selectedIds.length}
              sendableCount={sendableCount}
              sendDisabledReason={sendDisabledReason}
              sendNote={sendNote}
              findContactsCount={findContactsCount}
              onFindContacts={onFindContacts}
              findContactsBusy={findContactsBusy}
              retryCount={retryCount}
              onRetry={onRetryMany}
              retryBusy={retryBusy}
              retryNote={retryNote}
              canWrite={canWrite}
              onSendFirstTouch={() => onSendFirstTouch(selectedIds)}
              onRemove={() => onRemove(selectedIds)}
              onClear={onClearSelection}
              moveToBrand={moveToBrand}
            />
          ) : !loading ? (
            // Suppressed while loading — otherwise this briefly reads "0
            // creators" before the real total lands.
            <p className="text-sm font-medium text-(--foreground-secondary-default)">
              {totalCount} {totalCount === 1 ? 'creator' : 'creators'}
              {filtered ? ' · filtered' : ''}
            </p>
          ) : null}
          {/* v1 sends email only, so one channel mark here covers every row. */}
          <img src={gmailLogo} alt="Email" title="Email" className="ml-auto h-3.5 w-auto shrink-0" />
        </div>
        <DataTable<OutreachRow>
          columns={columns}
          data={rows}
          getRowId={(row) => row.entryId}
          loading={loading}
          // The row itself is clickable (rowClickAction). The creator name is its own, separate
          // click target for the profile (InboxCreatorCell). rowAriaLabel gives the row a fixed accessible name so
          // this role="button" row never substring-collides with an inner button's own name
          // query (e.g. "Find contact") — those buttons already stop propagation.
          onRowClick={(row) => {
            const action = rowClickAction(row, canWrite);
            if (action === 'wizard') onSendFirstTouch([row.entryId]);
            else if (action === 'waiting_draft') onOpenWaitingDraft(row.entryId);
            else if (action === 'add_email') onEditContact(row.entryId);
            else if (action === 'profile') onViewProfile(row.entryId);
            else onOpenRow(row.entryId);
          }}
          rowAriaLabel={(row) => `${ROW_CLICK_LABELS[rowClickAction(row, canWrite)]} ${row.displayName || row.username}`}
          rowSelection={{
            selectedIds: selectedSet,
            getRowLabel: (row) => row.username,
            onSelectionChange: (next) => {
              if (isSelectAllTransition(pageIds, selectedSet, next)) {
                onToggleSelectAll();
                return;
              }
              const [onlyId] = symmetricDifference(selectedSet, next);
              if (onlyId) onToggleSelect(onlyId);
            },
          }}
          // This only ever renders while `filtered` is true — a genuinely
          // empty tab (no query, no filter) is intercepted one level up by
          // OutreachTab's own rich EmptyState before OutreachRowsTable ever
          // mounts with zero rows.
          emptyState={
            <EmptyState
              icon={FileText}
              title="No match"
              body="Nothing matches the current filter or search."
              action={{ label: 'Clear filters', onClick: onClearFilters }}
            />
          }
          className="border-0 rounded-none bg-transparent"
        />
      </div>
    </div>
  );
}

/** One tooltip provider for the whole table, not one per row mark. */
export function OutreachRowsTable(props: OutreachRowsTableProps) {
  return (
    <StatusTooltipScope>
      <OutreachRowsTableBody {...props} />
    </StatusTooltipScope>
  );
}
